import { shortDate } from '../work/labels.js';

/**
 * Collections (2026-10-31): one case per customer, so a person sees Amy's whole caseload and where each account stands,
 * not only the items waiting on them. Status and the one-line situation are derived here from facts the caller gathers
 * (src/application/collections.ts). Pure: no database, clock or model.
 */
export const caseStatuses = ['needs_you', 'flagged', 'scheduled', 'waiting', 'paused', 'other', 'settled'] as const;
export type CaseStatus = typeof caseStatuses[number];
export const caseStatusLabels: Record<CaseStatus, string> = {
  needs_you: 'Needs you', flagged: 'Flagged', scheduled: 'Scheduled', waiting: 'Waiting on customer', paused: 'Paused',
  other: 'Nothing planned', settled: 'Settled',
};

export interface CaseItemFact { kind: 'approval' | 'reply' | 'task'; status: string; want: string; summary?: string | undefined; held: boolean }
export interface CaseBlockerFact {
  code: string; detail: string; owner: 'you' | 'amy' | 'customer'; subject: 'customer' | 'approval' | 'invoice' | 'workspace';
}
export interface CaseFacts {
  hasBalance: boolean;
  openInvoices: number;
  oldestDaysOverdue: number;
  items: CaseItemFact[];
  blockers: CaseBlockerFact[];
  badges: { paused: boolean; promise: boolean; brokenPromise: boolean; dispute: boolean };
  nextStep: { status: string };
  lastContact: { at: string; direction: 'in' | 'out' } | null;
  today: string;
}

/** Blockers a person must clear on this account. Workspace-wide ones (mailbox, sync) are shown once, not on every case. */
const personBlocker = (b: CaseBlockerFact) => b.owner === 'you' && b.subject !== 'workspace'
  && b.code !== 'needs_review' && b.code !== 'paused_manual';
const pauseCodes = new Set(['out_of_office', 'paused_manual']);
const waitCodes = new Set(['promise_active', 'payment_claimed']);
const moving = new Set(['due_today', 'next_window', 'ready', 'undo_wait']);

/**
 * First match wins. Needs you is exactly the items waiting on a person (a draft, a reply, a task), so it agrees with the
 * nav badge and Home; an account in trouble with nothing queued (broken promise, dispute, no working contact) is Flagged.
 */
export function caseStatus(f: CaseFacts): CaseStatus {
  if (f.items.length) return 'needs_you';
  if (f.badges.brokenPromise || f.badges.dispute || f.blockers.some(personBlocker)) return 'flagged';
  if (!f.hasBalance) return 'settled';
  if (f.badges.paused || f.nextStep.status === 'paused' || f.blockers.some(b => pauseCodes.has(b.code))) return 'paused';
  if (f.badges.promise || f.blockers.some(b => waitCodes.has(b.code))) return 'waiting';
  if (moving.has(f.nextStep.status)) return 'scheduled';
  if (f.nextStep.status === 'planned') return f.lastContact?.direction === 'out' ? 'waiting' : 'scheduled';
  return 'other';
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const clip = (text: string, max = 110) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

/**
 * One line a collector would say about the account, in AR words. A draft is the case's next step, not its situation, so a
 * drafted account is described by its invoices and its last contact.
 */
export function caseSituation(f: CaseFacts, status: CaseStatus): string {
  const reply = f.items.find(i => i.kind === 'reply');
  const task = f.items.find(i => i.kind === 'task');
  const person = f.blockers.find(personBlocker);
  const draft = f.items.find(i => i.kind === 'approval');
  if (status === 'needs_you') {
    if (reply) return clip(reply.summary ? `They replied: ${reply.summary}` : reply.want);
    if (person) return clip(person.detail);
    if (f.badges.brokenPromise) return 'Broke a promise to pay';
    if (task) return clip(task.want);
    const held = f.blockers.find(b => b.subject !== 'workspace' && b.code !== 'needs_review');
    if (held) return clip(held.detail);
    if (draft?.held) return clip(draft.want);
  }
  if (status === 'flagged') {
    if (f.badges.brokenPromise) return 'Broke a promise to pay';
    if (person) return clip(person.detail);
    const dispute = f.blockers.find(b => b.code === 'dispute_open');
    if (dispute) return clip(dispute.detail);
    return 'Disputed';
  }
  if (status === 'settled') return 'Paid up';
  if (status === 'paused' || status === 'waiting') {
    const reason = f.blockers.find(b => b.subject !== 'workspace' && (pauseCodes.has(b.code) || waitCodes.has(b.code)
      || b.code === 'dispute_open' || b.code === 'missing_po'));
    if (reason) return clip(reason.detail);
    if (status === 'paused') return 'Reminders paused';
    if (f.lastContact?.direction === 'out') return `Emailed ${shortDate(f.lastContact.at, f.today)}; no reply yet`;
  }
  const late = f.oldestDaysOverdue > 0
    ? `${plural(f.openInvoices, 'open invoice')}, oldest ${plural(f.oldestDaysOverdue, 'day')} late` : f.openInvoices ? `${plural(f.openInvoices, 'open invoice')}, none overdue yet` : 'Nothing open';
  const touch = !f.lastContact ? (f.oldestDaysOverdue > 0 ? '; no email yet' : '')
    : f.lastContact.direction === 'in' ? `; they wrote ${shortDate(f.lastContact.at, f.today)}`
      : `; last emailed ${shortDate(f.lastContact.at, f.today)}`;
  return `${late}${touch}`;
}
